import { useState } from "react";
import type { Lang } from "../lib/prefs";
import { useSites } from "../lib/sites";
import { usePersonas } from "../lib/personas";
import type {
  PersonaWithQueries,
  SuggestedPersona,
  CreatePersonaInput,
} from "../lib/personas";
import type { QueryKind } from "../lib/database.types";
import { ApiError } from "../lib/api";

const AVATARS = ["#2A6BF2", "#268A56", "#8A5CD8", "#B26B12", "#C0417A", "#0E8FA6"];

const T = {
  es: {
    title: "Perfiles (ICP)",
    sub: "a quién le preguntamos a la IA",
    hint: "Cada perfil es un comprador tipo + sus consultas a la IA. Expande para editar.",
    quotaP: "Perfiles",
    quotaQ: "Consultas",
    of: "de",
    suggest: "Sugerir con IA",
    suggesting: "Generando…",
    suggestTitle: "Sugerencias de IA",
    suggestSub: "Edita y guarda las que quieras. Nada se guarda hasta que lo apruebes.",
    save: "Guardar perfil",
    saving: "Guardando…",
    discard: "Descartar",
    addPersona: "＋ Añadir perfil",
    newPersona: "Nuevo perfil",
    pName: "Nombre del perfil",
    pDesc: "Descripción del perfil",
    pDirect: "Consultas directas (¿a dónde ir?)",
    pRanking: "Consultas de ranking (enlista las mejores)",
    addQ: "＋ Añadir consulta",
    del: "Eliminar",
    delPersona: "Eliminar perfil",
    savePersona: "Guardar cambios",
    capPersona: "Alcanzaste el límite de perfiles de tu plan.",
    capQuery: "Alcanzaste el límite de consultas de tu plan.",
    upgrade: "Mejora tu plan para agregar más.",
    provider: "generado por",
    llmErr: "No se pudo generar sugerencias. Intenta de nuevo.",
    genericErr: "Algo salió mal. Intenta de nuevo.",
    noSite: "Selecciona un sitio para administrar sus perfiles.",
    qphD: "p. ej. ¿qué lavandería a domicilio me recomiendas en Guadalajara?",
    qphR: "p. ej. enlista las mejores lavanderías a domicilio en Guadalajara",
    nameph: "p. ej. Sofía — dueña de lavandería",
    descph: "1 oración: quién es y qué busca",
  },
  en: {
    title: "Personas (ICP)",
    sub: "who we ask the AI about",
    hint: "Each persona is a buyer type + their AI queries. Expand to edit.",
    quotaP: "Personas",
    quotaQ: "Queries",
    of: "of",
    suggest: "Suggest with AI",
    suggesting: "Generating…",
    suggestTitle: "AI suggestions",
    suggestSub: "Edit and save the ones you want. Nothing is saved until you approve it.",
    save: "Save persona",
    saving: "Saving…",
    discard: "Discard",
    addPersona: "＋ Add persona",
    newPersona: "New persona",
    pName: "Persona name",
    pDesc: "Persona description",
    pDirect: "Direct queries (where should I go?)",
    pRanking: "Ranking queries (list the best)",
    addQ: "＋ Add query",
    del: "Remove",
    delPersona: "Delete persona",
    savePersona: "Save changes",
    capPersona: "You've reached your plan's persona limit.",
    capQuery: "You've reached your plan's query limit.",
    upgrade: "Upgrade your plan to add more.",
    provider: "generated by",
    llmErr: "Could not generate suggestions. Please try again.",
    genericErr: "Something went wrong. Please try again.",
    noSite: "Select a site to manage its personas.",
    qphD: "e.g. which on-demand laundry do you recommend in Guadalajara?",
    qphR: "e.g. list the best on-demand laundries in Guadalajara",
    nameph: "e.g. Sofía — laundromat owner",
    descph: "1 sentence: who they are and what they want",
  },
};

type Str = (typeof T)["es"];

export default function Personas({ lang, token }: { lang: Lang; token: string }) {
  const t = T[lang];
  const { selectedSite } = useSites();
  const p = usePersonas(token, selectedSite?.id ?? null);
  const [drafts, setDrafts] = useState<SuggestedPersona[] | null>(null);
  const [suggestProvider, setSuggestProvider] = useState<string>("");
  const [suggesting, setSuggesting] = useState(false);
  const [banner, setBanner] = useState<string | null>(null);

  if (!selectedSite) {
    return (
      <section className="view on">
        <div className="h-row">
          <h1>{t.title}</h1>
        </div>
        <p className="sub-line">{t.noSite}</p>
      </section>
    );
  }

  const personaAtCap = p.usage.personas >= p.caps.personas;
  const queryAtCap = p.usage.queries >= p.caps.queries;

  async function runSuggest() {
    setBanner(null);
    setSuggesting(true);
    try {
      const res = await p.suggest();
      setSuggestProvider(res.provider);
      setDrafts(res.personas);
    } catch {
      setBanner(t.llmErr);
    } finally {
      setSuggesting(false);
    }
  }

  async function saveDraft(index: number, input: CreatePersonaInput) {
    setBanner(null);
    try {
      await p.createPersona(input);
      setDrafts((d) => (d ? d.filter((_, i) => i !== index) : d));
    } catch (e) {
      setBanner(mapErr(e, t));
    }
  }

  function addBlankPersona() {
    setDrafts((d) => [
      ...(d ?? []),
      { name: "", description: "", queries: [{ text: "", kind: "direct" }, { text: "", kind: "ranking" }] },
    ]);
  }

  return (
    <section className="view on">
      <div className="h-row">
        <h1>{t.title}</h1>
        <span className="sub">{t.sub}</span>
      </div>
      <p className="sub-line">{t.hint}</p>

      <div className="quota-row">
        <span className={`quota-pill${personaAtCap ? " full" : ""}`}>
          {t.quotaP}: <b>{p.usage.personas}</b> {t.of} {p.caps.personas}
        </span>
        <span className={`quota-pill${queryAtCap ? " full" : ""}`}>
          {t.quotaQ}: <b>{p.usage.queries}</b> {t.of} {p.caps.queries}
        </span>
        <span className="sp" />
        <button className="btn btn-ghost" onClick={runSuggest} disabled={suggesting}>
          {suggesting ? t.suggesting : `✨ ${t.suggest}`}
        </button>
      </div>

      {banner && <p className="auth-err">{banner}</p>}

      {drafts && drafts.length > 0 && (
        <div className="card mb suggest-card">
          <div className="card-h">
            <h3>{t.suggestTitle}</h3>
            <span className="sub">
              {t.suggestSub}
              {suggestProvider && ` · ${t.provider} ${suggestProvider}`}
            </span>
          </div>
          <div style={{ padding: "0 16px 16px" }}>
            {drafts.map((d, i) => (
              <DraftCard
                key={i}
                t={t}
                draft={d}
                disabled={personaAtCap}
                onSave={(input) => saveDraft(i, input)}
                onDiscard={() => setDrafts((ds) => (ds ? ds.filter((_, j) => j !== i) : ds))}
              />
            ))}
          </div>
        </div>
      )}

      {p.error && <p className="auth-err">{t.genericErr}</p>}

      <div id="personaList">
        {p.personas.map((persona, i) => (
          <PersonaCard
            key={persona.id}
            t={t}
            persona={persona}
            color={AVATARS[i % AVATARS.length]}
            queryAtCap={queryAtCap}
            onUpdate={(patch) => p.updatePersona(persona.id, patch)}
            onDelete={() => p.deletePersona(persona.id)}
            onAddQuery={(q) => p.addQuery(persona.id, q)}
            onDeleteQuery={(id) => p.deleteQuery(id)}
            onErr={(e) => setBanner(mapErr(e, t))}
          />
        ))}
      </div>

      {personaAtCap ? (
        <p className="hint" style={{ marginTop: 4 }}>
          🔒 {t.capPersona} {t.upgrade}
        </p>
      ) : (
        <button className="btn btn-ghost" onClick={addBlankPersona}>
          {t.addPersona}
        </button>
      )}
    </section>
  );
}

function mapErr(e: unknown, t: Str): string {
  if (e instanceof ApiError) {
    if (e.code === "persona_cap") return t.capPersona + " " + t.upgrade;
    if (e.code === "query_cap") return t.capQuery + " " + t.upgrade;
  }
  return t.genericErr;
}

// A pre-filled AI suggestion (or a blank new persona) the user edits then saves.
function DraftCard({
  t,
  draft,
  disabled,
  onSave,
  onDiscard,
}: {
  t: Str;
  draft: SuggestedPersona;
  disabled: boolean;
  onSave: (input: CreatePersonaInput) => Promise<void>;
  onDiscard: () => void;
}) {
  const [name, setName] = useState(draft.name);
  const [description, setDescription] = useState(draft.description);
  const [direct, setDirect] = useState(
    draft.queries.find((q) => q.kind === "direct")?.text ?? "",
  );
  const [ranking, setRanking] = useState(
    draft.queries.find((q) => q.kind === "ranking")?.text ?? "",
  );
  const [busy, setBusy] = useState(false);

  async function save() {
    if (!name.trim()) return;
    setBusy(true);
    const queries: { text: string; kind: QueryKind }[] = [];
    if (direct.trim()) queries.push({ text: direct.trim(), kind: "direct" });
    if (ranking.trim()) queries.push({ text: ranking.trim(), kind: "ranking" });
    await onSave({ name: name.trim(), description: description.trim() || null, queries });
    setBusy(false);
  }

  return (
    <div className="draft">
      <div className="fld">
        <label>{t.pName}</label>
        <input className="inp" value={name} placeholder={t.nameph} onChange={(e) => setName(e.target.value)} />
      </div>
      <div className="fld">
        <label>{t.pDesc}</label>
        <input
          className="inp"
          value={description}
          placeholder={t.descph}
          onChange={(e) => setDescription(e.target.value)}
        />
      </div>
      <div className="qgrid" style={{ marginTop: 12 }}>
        <div>
          <label className="qlab">{t.pDirect}</label>
          <input className="inp" value={direct} placeholder={t.qphD} onChange={(e) => setDirect(e.target.value)} />
        </div>
        <div>
          <label className="qlab">{t.pRanking}</label>
          <input className="inp" value={ranking} placeholder={t.qphR} onChange={(e) => setRanking(e.target.value)} />
        </div>
      </div>
      <div className="p-actions">
        <button className="btn btn-primary" onClick={save} disabled={busy || disabled || !name.trim()}>
          {busy ? t.saving : t.save}
        </button>
        <button className="btn btn-ghost" onClick={onDiscard}>
          {t.discard}
        </button>
      </div>
      {disabled && <p className="hint" style={{ marginTop: 10 }}>🔒 {t.capPersona} {t.upgrade}</p>}
    </div>
  );
}

function PersonaCard({
  t,
  persona,
  color,
  queryAtCap,
  onUpdate,
  onDelete,
  onAddQuery,
  onDeleteQuery,
  onErr,
}: {
  t: Str;
  persona: PersonaWithQueries;
  color: string;
  queryAtCap: boolean;
  onUpdate: (patch: { name?: string; description?: string | null }) => Promise<void>;
  onDelete: () => Promise<void>;
  onAddQuery: (q: { text: string; kind: QueryKind }) => Promise<void>;
  onDeleteQuery: (id: string) => Promise<void>;
  onErr: (e: unknown) => void;
}) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(persona.name);
  const [description, setDescription] = useState(persona.description ?? "");
  const [savingMeta, setSavingMeta] = useState(false);

  const direct = persona.queries.filter((q) => q.kind === "direct");
  const ranking = persona.queries.filter((q) => q.kind === "ranking");
  const dirty = name !== persona.name || description !== (persona.description ?? "");

  async function saveMeta() {
    if (!name.trim()) return;
    setSavingMeta(true);
    try {
      await onUpdate({ name: name.trim(), description: description.trim() || null });
    } catch (e) {
      onErr(e);
    } finally {
      setSavingMeta(false);
    }
  }

  return (
    <div className={`p-card${open ? " open" : ""}`}>
      <div className="p-head">
        <span className="p-av" style={{ background: color }}>
          {(persona.name || "?").charAt(0).toUpperCase()}
        </span>
        <div className="p-toggle" onClick={() => setOpen((o) => !o)}>
          <div className="nm">{persona.name}</div>
          <div className="de">{persona.description || "—"}</div>
        </div>
        <span className="p-count">
          {persona.queries.length} {t.quotaQ.toLowerCase()}
        </span>
        <button
          className="del p-del"
          aria-label={t.delPersona}
          title={t.delPersona}
          onClick={() => onDelete().catch(onErr)}
        >
          ✕
        </button>
        <span className="chev" onClick={() => setOpen((o) => !o)}>
          ▸
        </span>
      </div>
      {open && (
        <div className="p-body">
          <div className="fld">
            <label>{t.pName}</label>
            <input className="inp" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="fld">
            <label>{t.pDesc}</label>
            <input className="inp" value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>
          <div className="qgrid" style={{ marginTop: 12 }}>
            <QueryColumn
              t={t}
              label={t.pDirect}
              kind="direct"
              rows={direct}
              placeholder={t.qphD}
              queryAtCap={queryAtCap}
              onAdd={onAddQuery}
              onDelete={onDeleteQuery}
              onErr={onErr}
            />
            <QueryColumn
              t={t}
              label={t.pRanking}
              kind="ranking"
              rows={ranking}
              placeholder={t.qphR}
              queryAtCap={queryAtCap}
              onAdd={onAddQuery}
              onDelete={onDeleteQuery}
              onErr={onErr}
            />
          </div>
          <div className="p-actions">
            <button className="btn btn-primary" onClick={saveMeta} disabled={!dirty || savingMeta || !name.trim()}>
              {savingMeta ? t.saving : t.savePersona}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function QueryColumn({
  t,
  label,
  kind,
  rows,
  placeholder,
  queryAtCap,
  onAdd,
  onDelete,
  onErr,
}: {
  t: Str;
  label: string;
  kind: QueryKind;
  rows: PersonaWithQueries["queries"];
  placeholder: string;
  queryAtCap: boolean;
  onAdd: (q: { text: string; kind: QueryKind }) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
  onErr: (e: unknown) => void;
}) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  async function add() {
    if (!text.trim()) return;
    setBusy(true);
    try {
      await onAdd({ text: text.trim(), kind });
      setText("");
    } catch (e) {
      onErr(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <label className="qlab">{label}</label>
      <div className="qlist">
        {rows.map((q) => (
          <div className="qrow" key={q.id}>
            <input className="inp" value={q.text} readOnly />
            <button className="del" aria-label={t.del} onClick={() => onDelete(q.id).catch(onErr)}>
              ✕
            </button>
          </div>
        ))}
      </div>
      {queryAtCap ? (
        <p className="hint" style={{ marginTop: 8 }}>🔒 {t.capQuery}</p>
      ) : (
        <div className="qrow" style={{ marginTop: 8 }}>
          <input
            className="inp"
            value={text}
            placeholder={placeholder}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") void add();
            }}
          />
          <button className="addq-btn" onClick={add} disabled={busy || !text.trim()} title={t.addQ}>
            ＋
          </button>
        </div>
      )}
    </div>
  );
}
